import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import type { AdminParcelProductRow } from "../interfaces/AdminParcelProduct";

const SHEET_NAME = "배송상품";

function normalizeCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  return String(v).trim();
}

/** 표시유무 열: 대소문자 무관 `Y` / `N` 만 허용 */
function parseDisplayYn(v: unknown): boolean {
  const s = normalizeCell(v).toUpperCase();
  if (s === "Y") return true;
  if (s === "N") return false;
  if (!s) return false;
  throw new Error(
    `표시유무는 Y 또는 N만 허용합니다. (입력: ${String(v)})`,
  );
}

/** 할인제외 열: 빈 칸은 N. Y/N만 허용 */
function parseDiscountExcludedYn(v: unknown): boolean {
  const s = normalizeCell(v).toUpperCase();
  if (!s || s === "N") return false;
  if (s === "Y") return true;
  throw new Error(
    `할인제외는 Y 또는 N만 허용합니다. (입력: ${String(v)})`,
  );
}

function parsePrice(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const s = normalizeCell(v).replace(/,/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/** 한글 헤더만 허용 */
function cell(obj: Record<string, unknown>, header: string): string {
  const direct = obj[header];
  if (direct !== undefined && direct !== "") return normalizeCell(direct);
  for (const [k, val] of Object.entries(obj)) {
    if (normalizeCell(k) === header) {
      return normalizeCell(val);
    }
  }
  return "";
}

function cellRaw(obj: Record<string, unknown>, header: string): unknown {
  const direct = obj[header];
  if (direct !== undefined && direct !== "") return direct;
  for (const [k, val] of Object.entries(obj)) {
    if (normalizeCell(k) === header) {
      return val;
    }
  }
  return "";
}

function apiRowToExcelRow(r: AdminParcelProductRow): Record<string, string | number> {
  return {
    상품코드: r.productCode,
    가격: r.price,
    이름: r.name,
    표시유무: r.isSearchable ? "Y" : "N",
    할인제외: r.isDiscountExcluded ? "Y" : "N",
  };
}

function emptyRow(): AdminParcelProductRow {
  return {
    productCode: "",
    price: 0,
    name: "",
    isSearchable: false,
    isDiscountExcluded: false,
  };
}

export function downloadParcelProductsExcel(
  rows: AdminParcelProductRow[],
  fileBaseName: string,
): void {
  const sheetRows = rows.map(apiRowToExcelRow);
  const worksheet = XLSX.utils.json_to_sheet(
    sheetRows.length > 0 ? sheetRows : [apiRowToExcelRow(emptyRow())],
  );
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, SHEET_NAME);
  const buf = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buf], { type: "application/octet-stream" });
  const day = new Date().toISOString().split("T")[0];
  saveAs(blob, `${fileBaseName}_${day}.xlsx`);
}

export async function parseParcelProductsExcel(
  file: File,
): Promise<AdminParcelProductRow[]> {
  const buf = await file.arrayBuffer();
  const workbook = XLSX.read(buf, { type: "array" });
  const sheet =
    workbook.Sheets[SHEET_NAME] ?? workbook.Sheets[workbook.SheetNames[0] ?? ""];
  if (!sheet) {
    throw new Error("시트를 찾을 수 없습니다.");
  }
  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: "",
    raw: false,
  });
  const rows: AdminParcelProductRow[] = [];
  for (const obj of raw) {
    const productCode = cell(obj, "상품코드");
    const price = parsePrice(cellRaw(obj, "가격"));
    const name = cell(obj, "이름");
    const isSearchable = parseDisplayYn(cell(obj, "표시유무"));
    const isDiscountExcluded = parseDiscountExcludedYn(cell(obj, "할인제외"));

    if (!productCode && !name && price === 0) {
      continue;
    }
    rows.push({
      productCode,
      price,
      name,
      isSearchable,
      isDiscountExcluded,
    });
  }
  return rows;
}
